import { CancelToken } from 'axios'

import { portalApi } from '@/axios'

import { Remittance, Transfer } from '@/classes/models'

export default class BusinessAccountGroupService {
  /**
   * Get all business account groups.
   *
   * @param {Context} context
   *
   * @return {Promise}
   */
  static getAll (context) {
    return portalApi.get(this.endpoint(context))
      .then(response => response.data) // Return raw data from API
      .catch(error => Promise.reject(error)) // Properly handle errors
  }

  static get ({ businessId, accountNumber }) {
    if (!businessId || !accountNumber) {
      return Promise.reject(new Error('Business ID and Account Number are required'))
    }

    const url = `businesses/${businessId}/business-accountgroups/${accountNumber}`

    return portalApi.get(url)
      .then(response => response.data)
      .catch(error => {
        return Promise.reject(error)
      })
  }

  /**
   * Gets remittance, for the AccountNumber belonging to the Business specified.
   *
   * @param {Context} context
   * @param {string} accountNumber
   *
   * @return {Promise}
   */
  static getRemittance (context, accountNumber) {
    let request = portalApi.get(`${this.endpoint(context)}/${accountNumber}/remittance`, { enableHandler: false }).then(response => {
      return response.data
    })

    return request.then(response => {
      // Map to Remittance
      return new Remittance(response)
    }).catch(error => {
      return Promise.reject(error)
    })
  }

  /**
   * Add an remittance, for the AccountNumber belonging to the Business specified.
   *
   * @param {Context} context
   * @param {string} accountNumber
   * @param {Object} remittance
   *
   * @return {Promise}
   */
  static addRemittance (context, accountNumber, remittance) {
    return portalApi.post(`${this.endpoint(context)}/${accountNumber}/remittance`, remittance)
  }

  /**
   * Gets the transfers that match the given search criteria, for the AccountNumber belonging to the Business specified.
   *
   * @param {Context} context
   * @param {string} accountNumber
   * @param {TableRequest} parameters
   *
   * @return {Promise}
   */
  static getTransfers (context, accountNumber, parameters) {
    const cancelToken = CancelToken.source()

    let request = portalApi.get(`${this.endpoint(context)}/${accountNumber}/transfers`, {
      cancelToken: cancelToken.token,
      params: parameters ? parameters.getParams() : null
    }).then(response => {
      return response.data
    })

    request.then(response => {
      // Set pagination
      response.totalCount = response.numberOfResults

      // Map to Transfer
      response.data = response.results.map(batch => {
        return new Transfer(batch)
      })

      return response
    }).catch(error => {
      return Promise.reject(error)
    })

    return { request, cancelToken }
  }

  /**
   * Gets the transfers report, that match the given search criteria, for the AccountNumber belonging to the Business specified
   *
   * @param {Context} context
   * @param {string} accountNumber
   * @param {object} parameters
   *
   * @return {Promise}
   */
  static getTransfersReports (context, accountNumber, parameters) {
    return portalApi.post(`${this.endpoint(context)}/${accountNumber}/transfers/reports`, null, { params: parameters || null })
  }

  static endpoint (context) {
    const businessId = context?.business?.id || context?.businessId

    if (!businessId) {
      throw new Error('Missing businessId in context:', context)
    }

    return `/businesses/${businessId}/business-accountgroups`
  }
}
